<template>
    <div>
        <div class="bg-liff">
            <div class="header-text">
                <div>
                    {{ $vuetify.lang.t('$vuetify.liffOvertimeRequest.title') }}
                </div>
                <span class="lang mr-3">
                    <change-lang />
                </span>
            </div>
            <div class="form-container">
                <template v-for="(child, key, index) in requestInfoComponents">
                    <component
                        :title="`${$vuetify.lang.t('$vuetify.liffOvertimeRequest.formTitle')} #${index + 1}`"
                        @data="onRequestInfosChange(key, index, $event)"
                        ref="forms"
                        class="form-item"
                        @remove="removeForm(key, index)"
                        :removable="isFormRemovalble"
                        :error-messages="formErrorMessages[index]"
                        :is="child"
                        :key="key"></component>
                </template>
            </div>
            <div class="footer">
                <div class="btn-add-request">
                    <v-btn
                        @click="onAddRequest"
                        width="46px"
                        height="46px"
                        color="primary"
                        fab>
                        <span class="fas fa-plus"></span>
                    </v-btn>
                </div>
                <div class="btn-container">
                    <div class="req-hrs-box">
                        <div>{{ $vuetify.lang.t('$vuetify.liffOvertimeRequest.totalReqHrsLabel') }}</div>
                        <div>{{ totalOTLabel }}</div>
                    </div>
                    <v-btn
                        @click="submit"
                        class="btn-default"
                        width="47.5%"
                        height="40px"
                        color="primary">
                        {{ $vuetify.lang.t('$vuetify.liff.submit') }}
                    </v-btn>
                </div>
            </div>
        </div>

        <liff-error-modal
            ref="errorModal"
            @ok-submit="closeWindow"
            :msg="errorMsg"
        />

        <info-modal
            ref="infoModal"
            @ok-submit="closeModal"
            :msg="infoMsg"
        />
    </div>
</template>

<script>
import LiffErrorModal from '@/components/modal/LiffErrorModal'
import InfoModal from '@/components/modal/InfoModal'
import ChangeLang from '@/components/liff/ChangeLang'
import OvertimeRequestForm from '@/components/liff/OvertimeRequestForm'
import axios from 'axios'

export default {
    name: 'OvertimeRequest',

    components: {
        LiffErrorModal,
        InfoModal,
        ChangeLang,
        OvertimeRequestForm
    },

    data () {
        return {
            totalMinutes: 0,
            form: {
                lineId: null,
                requestNo: null,
                requestInfos: []
            },
            requestInfoComponents: {},
            formErrorMessages: {},
            cacheSchedule: {},
            infoMsg: null,
            errorMsg: ''
        }
    },

    computed: {
        dateErrorMessages () {
            return {
                overlapLocal: this.$vuetify.lang.t('$vuetify.liffOvertimeRequest.overlapLocal')
            }
        },

        /**
         * transform totoal ot time in minutes to hours:minutes format
         */
        totalOTLabel () {
            const hours = Math.floor(this.totalMinutes / 60)
            const minutes = this.totalMinutes % 60
            const key = (hours > 1) ? 'hrs' : 'hr'
            const unit = this.$vuetify.lang.t(`$vuetify.liffOvertimeRequest.${key}`)

            if (minutes > 0) {
                return `${hours}.${minutes} ${unit}`
            } else if (hours > 0) {
                return `${hours} ${unit}`
            } else {
                return `0 ${unit}`
            }
        },

        /**
         * Form component will be removable only if there are more than 1 form
         */
        isFormRemovalble () {
            return this.form.requestInfos.length > 1
        }
    },

    methods: {
        closeWindow () {
            this.$liff.closeWindow()
        },

        errorModal (message) {
            this.$store.dispatch('setLoading', false)
            this.$refs.errorModal.dialog = true
            this.errorMsg = message
        },

        async initFormData () {
            this.form.lineId = await this.$util.getLineIdForLiff(process.env.VUE_APP_LINE_OT_REQUEST_LIFF)
            this.form.requestNo = Date.now().toString(32).toUpperCase()
            this.onAddRequest(false)
        },

        onAddRequest (checkform = true) {
            const formCount = this.form.requestInfos.length
            if (checkform && (formCount > 0)) {
                if (!this.canSubmit()) {
                    return
                }
            }

            this.form.requestInfos.push({})
            this.formErrorMessages[formCount] = {}
            this.requestInfoComponents[Date.now().toString(32)] = OvertimeRequestForm
            this.$forceUpdate()
            this.$nextTick(() => {
                const el = this.$refs.forms[this.form.requestInfos.length - 1].$el
                el.scrollIntoView({ behavior: 'smooth' })
            })
        },

        canSubmit () {
            const { forms } = this.$refs
            for (let i = 0; i < forms.length; i++) {
                if (!forms[i].canSubmit()) {
                    return false
                }
            }
            return true
        },

        async submit () {
            if (!this.canSubmit()) {
                return
            }

            this.$store.dispatch('setLoading', true)
            const res = await axios.post(`${process.env.VUE_APP_BACKEND}/overtime-request/create`, this.form)
            if (res && res.data && res.data.status === 'ok') {
                this.infoMsg = this.$vuetify.lang.t('$vuetify.liffOvertimeRequest.requestSuccess')
                this.$store.dispatch('setLoading', false)
                this.$refs.infoModal.dialog = true
            } else {
                this.errorModal(res.data.msg)
            }
        },

        onRequestInfosChange (key, index, info) {
            // clear cache at specify index eveytime that its value changed
            // to reduce work load from re-building all schedule everytime some index's value change
            delete this.cacheSchedule[index]

            // update from's info
            this.form.requestInfos[index] = this.transformData(index, info)

            // skip validation if changed info has no value or no startTime key
            if (Object.keys(info).length === 0) {
                return
            }

            // re-calculate total OT hours
            this.calculateTotalOT()

            // re-validate overlap
            this.checkAllScheduleOverlap()
        },

        /**
         * transform component data in to submitable form data
         */
        transformData (index, info) {
            const totalOTTime = this.$refs.forms[index].getOTTime()
            const stMinutes = this.$util.timeStringToMinute(info.startTime)
            const etMinutes = stMinutes + this.$util.timeStringToMinute(info.otTime)
            let endHours = parseInt(etMinutes / 60, 10)
            endHours = ((endHours >= 24) ? (endHours - 24) : endHours).toString().padStart(2, '0') // in case overnight ot
            const endMinutes = parseInt(etMinutes % 60, 10).toString().padStart(2, '0')
            const endTime = `${endHours}:${endMinutes}`

            return { ...info, otTime: totalOTTime, endTime }
        },

        isSchedulesOverlap (index, current, toCheck) {
            if (!current || !toCheck) { return }

            let hasError = false
            for (let i = 0; i < current.length; i++) {
                for (let j = 0; j < toCheck.length; j++) {
                    if (
                        ((toCheck[j].start <= current[i].start) && (current[i].start <= toCheck[j].end)) ||
                        ((toCheck[j].start <= current[i].end) && (current[i].end <= toCheck[j].end))
                    ) {
                        hasError = true
                        this.formErrorMessages[index] = {
                            startDate: this.dateErrorMessages.overlapLocal,
                            endDate: this.dateErrorMessages.overlapLocal,
                            startTime: this.dateErrorMessages.overlapLocal,
                            otTime: this.dateErrorMessages.overlapLocal
                        }
                    } else {
                        this.formErrorMessages[index] = {}
                    }

                    this.$forceUpdate()
                    if (hasError) {
                        return
                    }
                }
            }
        },

        /**
         * loop check schedules of changed info with others info
         */
        checkAllScheduleOverlap () {
            const dataLength = this.form.requestInfos.length
            for (let i = 0; i < dataLength; i++) {
                const currentSchedules = this.getOtSchedule(i)
                for (let j = 0; j < dataLength; j++) {
                    if (i === j) {
                        continue
                    }

                    const toCheckSchedules = this.getOtSchedule(j)
                    this.isSchedulesOverlap(i, currentSchedules, toCheckSchedules)
                }
            }
        },

        /**
         * Parse ranged-schedule to array of schedule from startDate to EndDate
         * - [cacheSchedule]    prevent excess parsing form data into schedule so schedule
         *                      will be recreate only when that form data was modified
         *                      or does not existed.
         * argument:
         *  index   index of schedule to be calculated
         */
        getOtSchedule (index) {
            const formData = this.form.requestInfos[index]
            if (!this.cacheSchedule[index]) {
                const schedules = []

                const currentDate = new Date(formData.startDate)
                const endDate = new Date(formData.endDate)
                const [startTimeHour, startTimeMinute] = formData.startTime.split(':')
                const otTimeHour = parseInt(formData.otTime / 60, 10)
                const otTimeMinute = formData.otTime % 60

                // loop create OT of every day in date range
                while (currentDate.toISOString() <= endDate.toISOString()) {
                    // start datetime
                    const start = new Date(currentDate.getTime())
                    start.setHours(startTimeHour)
                    start.setMinutes(startTimeMinute)

                    // end datetime
                    const end = new Date(start.getTime())
                    end.setHours(end.getHours() + otTimeHour)
                    end.setMinutes(end.getMinutes() + otTimeMinute)

                    schedules.push({ start, end })
                    currentDate.setDate(currentDate.getDate() + 1)
                }

                this.cacheSchedule[index] = schedules
            }

            return this.cacheSchedule[index]
        },

        /**
         * sum all ot time in minutes of all form
         */
        calculateTotalOT () {
            let total = 0
            const { forms } = this.$refs

            for (let i = 0; i < forms.length; i++) {
                total += forms[i].getOTTime()
            }

            this.totalMinutes = total
        },

        removeForm (key, index) {
            // clear all cache
            this.cacheSchedule = {}
            this.formErrorMessages = {}

            // remove this form data from the request
            delete this.requestInfoComponents[key]
            this.form.requestInfos.splice(index, 1)
            this.$forceUpdate()
            this.$nextTick(() => {
                // re-calculate total OT time
                this.calculateTotalOT()

                // re-validate the request
                this.checkAllScheduleOverlap()
            })
        },

        closeModal () {
            this.closeWindow()
        }
    },

    async mounted () {
        this.$store.dispatch('setLoading', true)

        try {
            await this.initFormData()
        } catch (error) {
            this.errorModal(this.$vuetify.lang.t('$vuetify.liffOvertimeRequest.cannotGetLineId'))
        }

        this.$store.dispatch('setLoading', false)
    }
}
</script>

<style lang="scss" scoped>
.bg-liff {
    height: 100vh;
    background-color: #2E2929;
    color: #FFFFFF;
    position: relative;

    .header-text {
        font-family: LexendDeca;
        font-size: 20px;
        padding: 1rem;
        height: 10%;
        min-height: 45px;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .form-container {
        background: #FFFFFF;
        border-top-right-radius: 30px;
        border-top-left-radius: 30px;
        color: #6C6C6C;
        padding: 1.5rem;
        font-size: 14px;
        font-weight: normal;
        overflow-y: auto;
        height: 80%;

        .form-item:not(:first-child) {
            margin-top: 1.5rem;
        }

        .form-item:last-child {
            margin-bottom: 1.5rem;
        }
    }

    .footer {
        width: 100%;
        color: #6C6C6C;
        display: flex;
        flex-flow: column;
        justify-content: flex-end;
        position: absolute;
        bottom: 0;
        background-image: linear-gradient(
            to bottom,
            rgba(255, 255, 255, 0),
            rgba(255, 255, 255, 0.8) 32%
        );

        .btn-add-request {
            display: flex;
            justify-content: flex-end;
            align-items: center;
            margin-bottom: 1rem;
            padding: 0rem 1.5rem;

            span {
                font-size: 1rem;
                padding-top: 0.25rem;
                color: white;
            }
        }

        .btn-container {
            display: flex;
            justify-content: space-between;
            border-radius: 5px;
            background-color: #FFFFFF;
            padding: 1rem 1.5rem 1rem 1.5rem;

            .btn-default {
                border-radius: 5px;
                box-shadow: 0 0 10px 0 rgba(215, 100, 54, 0.26);
            }

            .btn-outlined {
                border-width: 2px;
            }

            .req-hrs-box {
                @extend .ol-text;

                div:first-child {
                    color: #A8A8A8;
                    font-size: 14px;
                }

                div:last-child {
                    color: #131410;
                    font-size: 18px;
                    font-weight: bold;
                }
            }
        }
    }
}

@media screen and (orientation: landscape) {
    .bg-liff {
        display: flex;
        flex-direction: column;
        align-items: center;
    }
}
</style>
